/**
 * Sales-agent contracts.
 *
 * Two surfaces share this file:
 *
 * 1. **v2** (autonomous agent) — used by the four-tab native shell at
 *    `/sales/{today,leads,strategy,brain}`. Org-centric leads, evidence
 *    chains, weekly strategy memo, live brain feed. The backend agent
 *    self-bootstraps from xopnetworks.com + the AI Gateway knowledge
 *    base, plans its own missions, and writes everything it does into
 *    `agent_actions` so the Brain tab can stream the agent's
 *    consciousness back to a staff operator.
 *
 * 2. **v0** (legacy procurement-notice scan) — kept alive for the Brain
 *    tab's "scan feed" + "source health" widgets that already proved
 *    useful when the operator wants to know whether a particular tender
 *    portal is healthy. Everything below the v2 block is the v0 surface.
 *
 * Hard guardrail (both surfaces): the agent never contacts a lead. The
 * output is always a list a human can act on.
 */

/* ── v2 — autonomous agent ─────────────────────────────────────────────── */

/** What the agent box reports to the dashboard health pill. */
export type SalesAgentHealthCode =
  | "SALES_AGENT_OK"
  | "SALES_AGENT_BOOTSTRAPPING"
  | "SALES_AGENT_DEGRADED"
  | "SALES_AGENT_STALE"            // v4: master step hasn't ticked recently
  | "SALES_AGENT_NOT_CONFIGURED"
  | "SALES_AGENT_UNREACHABLE"
  | "SALES_AGENT_NO_DATA_YET";

export interface SalesAgentHealth {
  ok: boolean;
  code: SalesAgentHealthCode;
  upstream_url?: string;
  upstream_status?: number;
  upstream_body_snippet?: string;
  reason?: string;
  /** Whether the company-self-knowledge bootstrap has completed at
   * least once. The Today tab uses this to switch its hero copy. */
  has_company_profile?: boolean;
  /** Whether at least one strategy memo has been written. */
  has_strategy?: boolean;
  /** Whether at least one OrgLead has been surfaced. */
  has_leads?: boolean;
  /** v4 — freshness signals so the pill turns red when the agent
   * stops doing actual work, not just when the database is empty. */
  last_master_step_age_s?: number | null;
  last_lead_age_s?: number | null;
  gateway_ok?: boolean;
  searx_ok?: boolean;
  mission_queue?: { pending: number; running: number; done: number };
  /** v4 — operator paused the agent. While paused, no new Beat-fired
   * tasks run; the pill goes amber and freshness counters keep climbing
   * but the dashboard renders "Paused" instead of "Stale". */
  is_paused?: boolean;
  paused_reason?: string;
  /** Daily-budget "sleep" state. When the agent has spent its daily LLM
   * budget it skips all work until the reset — the pill shows "Sleeping"
   * (not "Agent healthy"). `budget_resets_at` is the ISO wake time. */
  budget_exhausted?: boolean;
  budget_resets_at?: string | null;
  /** Gateway-outage visibility. A `tick_error` row is written by the master
   * loop every time a reasoning call is rejected, so a non-zero
   * `recent_tick_errors` (last 15 min) is the difference between "the agent
   * is quiet because it has caught up" and "the agent cannot think". */
  last_tick_error?: string;
  last_tick_error_age_s?: number | null;
  recent_tick_errors?: number;
  db_ok?: boolean;
  gateway_error?: string;
  /** EdDSA outbound-signing posture. `stable_signing_key: false` means
   * api/worker/beat each self-generated a different ephemeral key, so the
   * worker's gateway tokens are rejected 401 and the autonomous loop cannot
   * ingest or reason — the agent looks healthy while doing nothing. Carries
   * the NAME of the PEM env var, never its value. */
  auth?: {
    issuer: string | null;
    stable_signing_key?: boolean;
    pem_env?: string;
    kid: string | null;
    detail?: string;
  };
  error?: string;
}

/** Operator pause/resume state. The dashboard's Sales header polls
 * `/api/sales/v2/agent/control` every 30 s so the button always
 * reflects the current state. */
export interface SalesAgentControl {
  is_paused: boolean;
  paused_at: string | null;
  paused_by: string;
  paused_reason: string;
  resumed_at: string | null;
  /** Which autonomous track(s) run: the signal-driven lead agent
   * ("leads"), the blind contact-harvester ("contacts"), or "both".
   * The Sales header renders a mode toggle next to Pause. */
  mode: SalesAgentMode;
}

/** The two autonomous tracks (or both). Switched via
 * `POST /api/sales/v2/agent/control/mode`. They share one budget + one
 * pause button — this picks who's working. */
export type SalesAgentMode = "leads" | "contacts" | "both";

/** The Lead Generator's standing hunt area (2026-09-23). While `active`, the
 * lead agent searches ONLY these countries (ISO-2) × verticals (canonical
 * slugs), does not promote the harvester pool, and refuses anything outside
 * the area before it is saved. Every candidate is also checked BEFORE it
 * becomes a lead; `refused_candidates` counts the ones that failed. Read via
 * `GET /api/sales/v2/agent/focus`, written via `POST` (sales:edit). */
export interface SalesLeadFocus {
  active: boolean;
  countries: string[];
  verticals: string[];
  brief: string;
  updated_by: string;
  updated_at: string;
  refused_candidates: number;
}

export type SalesLeadFocusInput = Pick<SalesLeadFocus, "active" | "countries" | "verticals" | "brief">;

/** Which agent a STEERING directive aims at (2026-06-21). A steer can shape the
 * signal-driven Lead Generator ("leads", the default — every pre-2026-06-21
 * note), the blind contact Harvester ("harvester" — it parses the note for
 * country/region + facility-type and restricts its sweep, e.g. "harvest just the
 * US"), or "both". Mirrors the agent's `SteeringNote.target`. */
export type SalesSteeringTarget = "leads" | "harvester" | "both";

/** Human labels for a steer target — the agent NAMES (matches the mode toggle +
 * the two-agent strip), not the raw track slug. */
export const STEER_TARGET_LABELS: Record<SalesSteeringTarget, string> = {
  leads: "Lead Generator",
  harvester: "Harvester",
  both: "Both agents",
};

/** Coerce any string (incl. a legacy/absent value) to a valid steer target —
 * defaults to "leads" so old directives render as Lead-Generator steers. */
export function steerTarget(value: string | null | undefined): SalesSteeringTarget {
  return value === "harvester" || value === "both" ? value : "leads";
}

/* ── Contact-harvester pool (Sales ▸ Contact Directory) ──────────────
 * The "contacts" track fills a raw, de-duplicated, industry-marked
 * directory of orgs + deep contacts. These are the read-only shapes the
 * dashboard browses (the agent writes; operators only read). */

export interface SalesHarvestContact {
  id: string;
  full_name: string;
  title: string;
  role_summary: string;
  seniority: "exec" | "director" | "manager" | "staff" | "unknown";
  email: string | null;
  email_confidence: "unknown" | "guessed" | "likely" | "verified";
  /** Paid-verifier verdict — the authoritative "is this email real?" signal the
   * UI badges. "unverified" = not checked; "valid"/"invalid"/"catchall" from the
   * verifier; "catchall" = domain accepts every address (can't confirm). */
  email_status: "unverified" | "valid" | "invalid" | "catchall" | "unknown";
  phone: string | null;
  linkedin_url: string | null;
  source_url: string;
  /** Industry mark — every contact carries its org's vertical + campaign. */
  vertical: string;
  campaign: string;
}

/** A contact row in the flat Contacts view — its org is denormalized in. */
export interface SalesHarvestContactRow extends SalesHarvestContact {
  org_id: string;
  org_name: string;
  org_country: string;
}

export interface SalesHarvestOrg {
  id: string;
  name: string;
  country: string;
  region: string;
  domain: string | null;
  phone: string | null;
  vertical: string;
  campaign: string;
  status: string;
  /** Lead-consumption state: the lead agent drains the pool into scored
   * leads — "pending" (not yet evaluated) | "promoted" (became an org_lead) |
   * "rejected" (evaluated, not an XOP fit) | "exists" (already a known
   * lead/CRM org). */
  promotion_state: string;
  promoted_lead_id: string | null;
  source_url: string;
  contact_count: number;
  harvested_at: string;
}

export interface SalesHarvestOrgDetail extends SalesHarvestOrg {
  address: string | null;
  contacts: SalesHarvestContact[];
}

export interface SalesHarvestOrgsPayload {
  as_of: string;
  total: number;
  page: number;
  per_page: number;
  items: SalesHarvestOrg[];
}

export interface SalesHarvestContactsPayload {
  as_of: string;
  total: number;
  page: number;
  per_page: number;
  items: SalesHarvestContactRow[];
}

/** One line of the harvester's "thinking" — a log entry from its enumerate /
 * dig / dedup / consume work. Drives the Contact Directory activity feed. */
export interface SalesHarvestActivityItem {
  id: number;
  ts: string;
  scope: string;
  level: string;
  message: string;
}

export interface SalesHarvestActivityPayload {
  as_of: string;
  items: SalesHarvestActivityItem[];
}

/* ── The harvester's strategy (the Strategy tab shows this in contacts mode) ── */
export interface SalesHarvestStrategyVertical {
  vertical: string;
  label: string;
  sample_queries: string[];
  registries: string[];
}

export interface SalesHarvestStrategyCampaign {
  slug: string;
  label: string;
  regions: string[];
  verticals: SalesHarvestStrategyVertical[];
  matrix_cells: number;
  cells_done: number;
  cells_pending: number;
}

export interface SalesHarvestStrategy {
  as_of: string;
  mission: string;
  method: string[];
  rules: string[];
  campaigns: SalesHarvestStrategyCampaign[];
  verifier_enabled: boolean;
  re_dig_days: number;
}

export interface SalesHarvestStats {
  as_of: string;
  total_orgs: number;
  total_contacts: number;
  contacts_with_email: number;
  contacts_with_phone: number;
  orgs_by_vertical: Record<string, number>;
  orgs_by_status: Record<string, number>;
  /** Pool-drain marker histogram: pending / promoted / rejected / exists
   * counts. NOT a lead count — `promotion_state` is stamped once and never
   * reverted, and "promoted" also covers dedup-links to a pre-existing lead
   * (no new lead minted). For the real "Leads created" headline use
   * `leads_created`. */
  orgs_by_promotion: Record<string, number>;
  promoted_orgs: number;
  /** The honest "Leads created" headline — leads the harvester actually minted
   * (`OrgLead.source === "harvester"`), counted off the leads table so it can
   * never drift from the Leads tab. A true subset of the Leads "All" count;
   * filter the Leads tab to "Found by: Harvester" to see the same set. */
  leads_created: number;
  contacts_by_vertical: Record<string, number>;
  /** Email-certainty roll-up: contact counts by email_status. */
  contacts_by_email_status: Record<string, number>;
  /** Whether a paid email verifier is configured (key present). */
  verifier_enabled: boolean;
  last_harvested_at: string | null;
}

/** Vertical the agent assigned to a lead. The legacy 8-value enum was
 * dropped in v4 — the senior-manager persona names verticals in its
 * own words, so this is now a free-form string. The UI's
 * `<VerticalBadge>` colour-codes the legacy vertical strings and
 * falls back to a neutral colour for novel ones. */
export type SalesVertical = string;

/** Kinds of evidence the legacy enum recognised. New free-text
 * evidence kinds are still allowed — the senior-manager persona
 * names them itself, and the UI renders them as a string badge.
 * Kept as a `string` for the same reason as `SalesVertical` above. */
export type SalesSignalKind = string;

/** Operator-facing urgency. Separate from `confidence` (fit) — a
 * lead can be a great fit but cold (no current trigger), or a
 * marginal fit that's on fire (active RFP). The Today tab sorts
 * by heat, not confidence — operators care about *when*. */
export type SalesLeadHeat = "cold" | "warm" | "hot" | "on_fire";

/** The kind of dated buying window a lead's `deadline` represents, so the UI
 * can caption it ("RFP due" / "Event ends" / "Contract expiry" / …). These are
 * the values the agent emits; an unmapped value falls back to a generic
 * "Deadline" caption in `deadlineKindLabel`. */
export type SalesDeadlineKind =
  | "rfp"
  | "event"
  | "contract_end"
  | "eol"
  | "registration"
  | "other";

/** Email confidence on a Person attached to a lead. */
export type SalesEmailConfidence =
  | "unknown"
  | "guessed"
  | "likely"
  | "verified";

/** A named buyer attached to an OrgLead (v4). */
export interface SalesPerson {
  id: string;
  full_name: string;
  title: string;
  role_summary: string;
  source_url: string;
  linkedin_url?: string | null;
  email?: string | null;
  email_confidence: SalesEmailConfidence;
  /** A direct phone line if the page listed one beside this buyer. Grounded
   * at enrichment (the number must literally appear on the site). */
  phone?: string | null;
}

/** The senior-manager dossier for a lead (v4). */
export interface SalesLeadBrief {
  id: string;
  version: number;
  why_now_markdown: string;
  xop_angle: string;
  suggested_first_message: string;
  suggested_contact_path: string;
  /** Pinned evidence URLs the brief leans on. */
  evidence: Array<{ title: string; url: string }>;
  generated_at?: string | null;
}

/** A single piece of evidence the agent assembled. */
export interface SalesSignal {
  id: string;
  kind: SalesSignalKind;
  source_url: string;
  source_title: string;
  /** ISO 8601 — when the *evidence event* happened, as best the agent
   * can infer (article publication, tender post date, etc.). Falls
   * back to capture time when the source has no date. */
  captured_at: string;
  /** 1–2 sentence neutral summary of the evidence. */
  summary: string;
  /** Why the agent thinks this evidence implies XOP fit. Renders in
   * the lead drawer beside the source link. */
  agent_reasoning: string;
}

/**
 * The lead's pipeline stage. Mirrors the agent's `OrgLeadStatus` enum
 * (`xop-agents/apps/sales/app/models/org_lead.py`) + the `org_lead_status`
 * Postgres enum — the agent serialises `lead.status.value` straight onto the
 * card. Operator-settable stages (`new` → `qualified` → `contacted` / `denied`,
 * plus `dismissed` = archived) move a lead between the Leads-page tabs;
 * `exported` is set only by the CRM-export flow, not a manual move. Keep this
 * union in lockstep with the agent enum — a missing member turns the LeadPage
 * `LifecycleBar` comparisons into TS2367 "no overlap" errors. Pinned by
 * `sales.test.ts`.
 */
export type SalesOrgLeadStatus =
  | "new"
  | "qualified"
  | "contacted"
  | "denied"
  | "dismissed"
  | "exported";

/**
 * Which agent created the lead (2026-06-20). Both bots feed the ONE Leads
 * pipeline — this records the origin so the operator can see who found each
 * lead and filter to just one bot's output:
 *   • "discovery" — the signal-driven LEAD GENERATOR found it on its world scan.
 *   • "harvester" — the lead agent PROMOTED it from the HARVESTER's pool.
 * Mirrors the agent's `org_leads.source`; absent/old rows read as "discovery".
 */
export type SalesLeadSource = "discovery" | "harvester";

/** Friendly agent name per source — matches the names used across the Sales
 * section (the Status two-agent strip, the harvester directory). */
export const LEAD_SOURCE_LABELS: Record<SalesLeadSource, string> = {
  discovery: "Lead Generator",
  harvester: "Harvester",
};

/** One-line "where it came from" caption for the lead detail / filter help. */
export const LEAD_SOURCE_DESCRIPTIONS: Record<SalesLeadSource, string> = {
  discovery: "Found by the Lead Generator on its own signal-driven world scan.",
  harvester: "Promoted into a lead from the Harvester's contact pool.",
};

/** Normalise an optional/unknown wire value to a known source (defaults to the
 * lead agent's own discovery, which is what every pre-`source` row was). */
export function leadSource(value: string | null | undefined): SalesLeadSource {
  return value === "harvester" ? "harvester" : "discovery";
}

export interface SalesOrgLeadCard {
  id: string;
  name: string;
  country: string;
  vertical: SalesVertical;
  /** 0..1 confidence the agent has that XOP could win this org. */
  confidence: number;
  status: SalesOrgLeadStatus;
  /** v4 — operator-facing urgency. The Today tab sorts by this. */
  heat: SalesLeadHeat;
  /** v6 — WHY this is an opportunity, set by the judge:
   *   • "trigger" — a dated public buying event (RFP / EOL / incident /
   *     regulation / new build). Can be HOT.
   *   • "fit" — a specific, EVIDENCED evergreen operational match found by
   *     solution-led discovery (e.g. an auto-parts distributor that fits
   *     Hoot-n-Holler). Capped at WARM. Defaults to "trigger" for older leads. */
  lead_kind?: "trigger" | "fit";
  /** Which agent created this lead — see {@link SalesLeadSource}. Optional on
   * the wire (absent on pre-2026-06-20 rows) — read via {@link leadSource}. */
  source?: SalesLeadSource;
  /** v4 — primary internet domain ("lufthansa-technik.com"). */
  domain?: string | null;
  /** v4 — XOP product bundle the senior-manager picked for this org.
   * No longer shown as a column on the Leads list (it just echoed every product
   * and conveyed nothing — dropped 2026-06-16); still rendered on the lead
   * detail page + the shared PDF brief. */
  recommended_bundle: string;
  /** v11 — operator-list placeholder columns (2026-06-16). The Leads list shows
   * a column for each; until a data source exists they render blank. Optional +
   * absent on the wire today — the agent does not yet emit them. When it later
   * captures a prospect's stated PROJECT TIMELINE / BUDGET (e.g. from an RFP) or
   * a computed LEAD SCORE (formula TBD), populating these lights the columns up
   * with no UI change. `project_timeline` / `project_budget` are free-text
   * human captions ("Q3 2026", "$250k–$400k"); `lead_score` is a 0..100 number. */
  project_timeline?: string | null;
  project_budget?: string | null;
  lead_score?: number | null;
  /** v9 — the RFP / tender RESPONSE deadline (calendar date bids are DUE) when
   * the trigger is a dated procurement notice; `null`/absent otherwise. ISO
   * `YYYY-MM-DD`. `rfp_expired` is the SERVER's verdict (deadline strictly
   * before today, UTC) so the UI needn't re-derive it. Note: `heat` is already
   * COOLED for an expired RFP (the agent caps it at warm), so a closed bid
   * window never reads as on-fire — the date + `rfp_expired` explain why it's
   * cooled, while the lead keeps all its contact + fit value. */
  rfp_deadline?: string | null;
  rfp_expired?: boolean;
  /** v10 — generalized deadline (supersedes `rfp_deadline`). The dated buying
   * window's due date for ANY trigger kind — an RFP/tender, an ending event, a
   * contract / EOL date, a registration cutoff — as ISO `YYYY-MM-DD`;
   * `null`/absent when the trigger has no date. `deadline_kind` names the source
   * so the UI can label it; `deadline_label` is an optional short human caption
   * ("Conference ends"). `rfp_deadline`/`rfp_expired` above are retained as
   * compat aliases the server derives from this (set only when
   * `deadline_kind === "rfp"`). Heat is already cooled for an expired deadline
   * (capped at warm). This is the input to the derived {@link leadUrgency} tier
   * — the "super hot" / "last chance" signal. */
  deadline?: string | null;
  deadline_kind?: SalesDeadlineKind | null;
  deadline_label?: string | null;
  /** v12 — deadline VERIFICATION ("100% proof", 2026-06-16). The agent's date
   * was taken on faith and three prod leads showed a red "act in N days"
   * countdown on dates the cited source never stated, or on a forecast RFP
   * *release* / NRC *inspection* date that is not an act-by deadline. The judge
   * now fetches the cited source and confirms the date appears VERBATIM on the
   * page in a due/closing context (server `deadline_verification`):
   *   • `true`  — proven; `deadline_proof` holds the verbatim source sentence.
   *   • `false` — checked + NOT proven (date absent / a release-not-due date).
   *   • `null`/absent — not yet re-checked.
   * {@link leadUrgency} fires ONLY when this is `true` (fail-closed): a date
   * that is `false`/`null` renders WITHOUT a countdown. `deadline_proof` is the
   * verbatim proof sentence (verified) or a short reason it couldn't be proven,
   * shown on the lead detail page. */
  deadline_verified?: boolean | null;
  deadline_proof?: string | null;
  /** The independent quality judge's verdict, serialized onto the LIST card
   * (not just the detail) so the closing-soon pulse + alerts can gate to
   * good-fit leads (`keep` confirmed / `review` "worth a look"), excluding
   * `reject` / unscored. `null` = not yet judged. */
  eval_verdict?: "keep" | "review" | "reject" | null;
  /** 1-line "why" for the Today tab card. */
  agent_summary: string;
  /** ISO 8601 — most recent signal, drives "fresh" sorting. */
  last_signal_at: string;
  /** ISO 8601 — when the lead was added to the pipeline. Always set
   * (unlike `last_signal_at`). The dashboard "Leads pending review"
   * pulse stamps "added Xh ago" and sorts newest-added from this. */
  created_at: string;
  /** Distinct kinds attached to this org so the card can show a row
   * of small badges without fetching the full evidence. */
  signal_kinds: SalesSignalKind[];
  /** CRM outcome loop. `crm_pushed_at` — the operator pushed this lead into the
   * CRM (it's now a CRM Lead; a strong "good lead" signal). `converted_at` — a
   * sweep matched the org to a CRM *customer*, i.e. it converted (the strongest
   * signal; the UI shows a "Converted to customer" badge). Both ISO strings. */
  crm_pushed_at?: string | null;
  converted_at?: string | null;
}

/* ── Deadline urgency ("super hot") — derived, never stored ─────────────── */

/** Days within which a live, non-expired deadline makes a lead "super hot". */
export const URGENCY_SUPER_HOT_DAYS = 14;
/** Days within which it escalates to the louder "last chance" tier. */
export const URGENCY_LAST_CHANCE_DAYS = 3;

/** Operator-facing deadline urgency, computed LIVE from `deadline − today` and
 * kept orthogonal to `heat`. `none` = no live near deadline. */
export type SalesLeadUrgencyTier = "none" | "super_hot" | "last_chance";

export interface SalesLeadUrgency {
  tier: SalesLeadUrgencyTier;
  /** Whole days to the deadline (0 = due today); `null` when no deadline applies. */
  daysLeft: number | null;
  /** Short operator caption WITH the tier word ("Super hot · act within 9 days" /
   * "Last chance · due today"); `null` when the tier is "none". Used where the
   * tier word adds context (the dashboard pulse + notification meta). */
  label: string | null;
  /** The tier-FREE action caption ("Act within 12 days" / "Due today" /
   * "3 days left"); `null` when the tier is "none". The on-screen `UrgencyBadge`
   * shows THIS — the tier word ("SUPER HOT"/"LAST CHANCE") lives in the heat pill
   * (see the web's `leadHeatDisplay`), not in the badge text, so the two split
   * the caption instead of repeating it. */
  action: string | null;
}

/** The lead statuses that are still "live" — a denied/dismissed lead is never
 * urgent (the operator already moved on). */
const LIVE_URGENCY_STATUSES: readonly SalesOrgLeadStatus[] = [
  "new",
  "qualified",
  "contacted",
  "exported",
];

/** Parse an ISO `YYYY-MM-DD` (optionally with a trailing time) into a
 * UTC-midnight epoch, or `null` for empty / malformed / impossible dates. */
function parseIsoDateUtc(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const da = Number(m[3]);
  const ms = Date.UTC(y, mo - 1, da);
  const back = new Date(ms);
  if (
    back.getUTCFullYear() !== y ||
    back.getUTCMonth() !== mo - 1 ||
    back.getUTCDate() !== da
  ) {
    return null; // e.g. 2026-02-31 rolled over
  }
  return ms;
}

function utcMidnight(d: Date): number {
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

function pluralDays(n: number): string {
  return `${n} day${n === 1 ? "" : "s"}`;
}

/**
 * The single source of truth for "super hot". Pure + timezone-stable: `now` is
 * injected so it's deterministic in tests and identical across the web, the
 * server pulse, and the notifications dispatcher. Reads the generic `deadline`,
 * falling back to the legacy `rfp_deadline` for rows not yet re-serialized.
 *
 * Returns `none` unless the lead is LIVE (not denied/dismissed), the deadline is
 * VERIFIED (`deadline_verified === true` — proven against the cited source; see
 * the "100% proof" gate), has a parseable deadline, and that deadline is
 * today-or-future (an expired window is a caution, not urgent — the agent
 * already cooled its heat). Otherwise the day count maps to a tier:
 * `0..3 → last_chance`, `4..14 → super_hot`.
 *
 * The verification gate is FAIL-CLOSED: a date that was never proven on its
 * source (`deadline_verified` false/null/absent) shows WITHOUT a countdown,
 * because an unproven date is exactly the false "super hot" the operator hit
 * (a forecast release date, a hallucinated date, a regulatory inspection date).
 */
export function leadUrgency(
  lead: Pick<
    SalesOrgLeadCard,
    "status" | "deadline" | "rfp_deadline" | "deadline_verified"
  >,
  now: Date,
): SalesLeadUrgency {
  const none: SalesLeadUrgency = {
    tier: "none",
    daysLeft: null,
    label: null,
    action: null,
  };
  if (!LIVE_URGENCY_STATUSES.includes(lead.status)) return none;

  // "100% proof" gate: only a deadline proven against its cited source may
  // drive urgency. Unproven / not-yet-checked → no countdown (fail-closed).
  if (lead.deadline_verified !== true) return none;

  const due = parseIsoDateUtc(lead.deadline ?? lead.rfp_deadline ?? null);
  if (due === null) return none;

  const daysLeft = Math.round((due - utcMidnight(now)) / 86_400_000);
  if (daysLeft < 0 || daysLeft > URGENCY_SUPER_HOT_DAYS) return none;

  if (daysLeft <= URGENCY_LAST_CHANCE_DAYS) {
    const action = daysLeft === 0 ? "Due today" : `${pluralDays(daysLeft)} left`;
    return {
      tier: "last_chance",
      daysLeft,
      action,
      label: `Last chance · ${action.toLowerCase()}`,
    };
  }
  const action = `Act within ${pluralDays(daysLeft)}`;
  return {
    tier: "super_hot",
    daysLeft,
    action,
    label: `Super hot · ${action.toLowerCase()}`,
  };
}

export interface SalesOrgLeadDetail extends SalesOrgLeadCard {
  /** Full multi-paragraph reasoning the agent wrote when the lead
   * was created or last updated. Kept as a fallback for legacy reads
   * — the canonical multi-paragraph dossier is in `briefs[0]`. */
  agent_full_reasoning: string;
  /** The org's MAIN / general / switchboard telephone, harvested from the
   * website footer / contact page (grounded — never an LLM guess). The
   * reach-by-phone floor: a lead with a name but no email is still callable
   * via the front desk. `null` when the site listed no usable number. */
  phone?: string | null;
  /** Every signal attached, ordered newest→oldest. */
  signals: SalesSignal[];
  /** v4 — named buyers found during deep_investigate. */
  persons: SalesPerson[];
  /** v4 — versioned briefs (latest first). The lead drawer renders
   * `briefs[0]` as the why-now header + suggested first message. */
  briefs: SalesLeadBrief[];
  created_at: string;
  updated_at: string;

  /** v5 — the lead-quality judge's INDEPENDENT second opinion (a separate
   * LLM from the agent that wrote the lead). `null` until the lead has been
   * judged (`eval_at`). The lead page turns these into the "why this is
   * confirmed + passed every quality check" panel. */
  /** 0..1 — is the trigger publicly provable (grounded in a cited source)? */
  eval_grounding?: number | null;
  /** 0..1 — does it match an XOP product / vertical? */
  eval_fit?: number | null;
  /** 0..1 — is there a dated public buying trigger (a real CURRENT need), not
   * just topical fit? A lead is "confirmed" only when grounded + fits + needs. */
  eval_need?: number | null;
  /** The judge's call. `null` = not yet reviewed. */
  eval_verdict?: "keep" | "review" | "reject" | null;
  /** One-paragraph justification from the judge. */
  eval_reason?: string;
  /** ISO 8601 — when the judge scored it. `null` = pending review. */
  eval_at?: string | null;
  /** 0..1 — the confidence floor a written lead had to clear, so the UI can
   * show "72% — above the 35% bar". */
  confidence_floor?: number;
}

export interface SalesLeadsListPayload {
  as_of: string;
  total: number;
  page: number;
  per_page: number;
  items: SalesOrgLeadCard[];
}

/* ── Share a lead by email (PDF attached) ──────────────────────────────── */

/** A suggested recipient for the "Share lead" modal — a dashboard user with a
 * sales-facing role. The directory only stores the email (no real display
 * name), so the UI shows the email + its role. */
export interface SalesShareRecipient {
  email: string;
  /** `sales_manager` | `super_admin` — the only roles offered as recipients. */
  role: string;
}

export interface SalesShareRecipientsResponse {
  recipients: SalesShareRecipient[];
}

/** Hard ceiling on the DECODED share-PDF attachment, enforced on BOTH sides:
 * the client (`ShareLeadDialog`) refuses to upload a capture larger than this,
 * and the server (`routes/sales.ts`) rejects one with `SALES_SHARE_INVALID_PDF`.
 *
 * Why both: the route's Fastify `bodyLimit` (≈ this × 4/3 for base64 + JSON
 * envelope) rejects an over-limit body by RESETTING the connection mid-upload,
 * which surfaces to the browser as a bare network error ("Load failed" in
 * WebKit, "Failed to fetch" in Chrome) with no typed code — so the client must
 * pre-check the size and show a legible message instead of letting the upload
 * blow the limit. Keeping the cap here keeps the two sides in lockstep. */
export const SALES_SHARE_MAX_PDF_BYTES = 8 * 1024 * 1024;

/** Body for `POST /api/sales/leads/:id/share`. The PDF is generated client-side
 * (jsPDF) and uploaded base64-encoded — the dashboard server attaches it and
 * sends via the shared Mailer. `lead_name` is display-only (subject/body). */
export interface SalesShareLeadRequest {
  /** Primary recipients — at least one; (to + cc) ≤ 25. */
  to: string[];
  cc?: string[];
  subject: string;
  /** Optional operator message wrapped into the email body. */
  message?: string;
  /** The lead's display name — used in the email body/subject only. */
  lead_name: string;
  /** Attachment filename (server re-sanitises + enforces a `.pdf` suffix). */
  filename: string;
  /** Base64-encoded PDF bytes (no data-URI prefix). */
  pdf_base64: string;
}

export interface SalesShareLeadResponse {
  ok: true;
  /** How many addresses the email was sent to (to + cc). */
  sent_to: number;
}

/** One contact person carried across when a lead is converted to a CRM
 * prospect. Maps onto a CRM `customer_contacts` row. The agent may find
 * several named buyers per lead — ALL of them travel, plus any generic
 * org email (info@/sales@) so the operator never lands on an empty
 * contact list. Every field is optional: an email-only contact (just a
 * generic inbox, no name) is valid. */
export interface SalesAddToCrmContact {
  first_name?: string;
  last_name?: string;
  email?: string;
  job_title?: string;
  phone?: string;
  /** How trustworthy this contact's email is (the agent's confidence ladder:
   * unknown < guessed < likely < verified). Carried across so the CRM can flag
   * guessed-vs-verified addresses in the email-campaign recipient picker and
   * default-select only the verified ones. Omitted for prose-scraped generic
   * inboxes where confidence is unknown. Stored on `customer_contacts`. */
  email_confidence?: SalesEmailConfidence;
}

/** Body for `POST /api/sales/v2/leads/:id/add-to-crm`. The scalar
 * `first_name`/`last_name`/`email`/`job_title` remain for backward
 * compatibility (a single primary contact); new callers send the full
 * `contacts` array so EVERY named buyer + generic email moves across. */
export interface SalesAddToCrmRequest {
  company_name: string;
  website?: string;
  industry?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  job_title?: string;
  notes?: string;
  /** Every contact to create on the new CRM prospect. When present and
   * non-empty this supersedes the scalar single-contact fields. */
  contacts?: SalesAddToCrmContact[];
}

/** What the autonomous loop is currently working on. */
export type SalesMissionKind =
  | "vertical_sweep"
  | "org_deepdive"
  | "signal_chase"
  | "tender_match"
  | "competitor_watch"
  | "market_recon";

export type SalesMissionStatus = "pending" | "running" | "done" | "failed";

export interface SalesMission {
  id: string;
  kind: SalesMissionKind;
  vertical: SalesVertical | null;
  geography: string | null;
  /** 1-sentence hypothesis the agent wrote when it queued the mission.
   * E.g. "Trader-voice replacements at second-tier banks in Brazil
   * after Q1 regulatory updates." */
  hypothesis: string;
  status: SalesMissionStatus;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
}

/** Kind of a Brain-tab activity row. Free-form `string` for the same
 * reason as `SalesVertical` / `SalesSignalKind` above: the v4 agent
 * emits more kinds than the legacy four (`master_step`,
 * `self_knowledge_refreshed`, `strategy_regenerated`, `tick_error`, …)
 * and may add more. The feed switches on the known ones and falls
 * back to a plain-summary row for anything else. */
export type SalesAgentActionKind = string;

export interface SalesAgentAction {
  id: string;
  mission_id: string | null;
  /** Human-readable mission label so the feed doesn't need to join. */
  mission_label: string | null;
  step_index: number;
  kind: SalesAgentActionKind;
  /** The interesting bit — what Claude said, what URL was fetched,
   * what observation was recorded. The Brain tab uses this as the
   * row headline. */
  summary: string;
  /** v4 — full structured payload. The Brain tab uses this to render
   * the actual content of each step:
   *   - plan rows:    payload.plan = {tool, query?, url?, …} + payload.reflection
   *   - observe rows: payload.observation = {label, results?, hits?, text?, …}
   *   - master_step:  payload = {tool, args, reflection, result_head, ts}
   *   - reflect rows: summary already carries the reflection text;
   *                   payload is empty.
   * The shape per kind is intentionally not enum-typed — the agent
   * may add new kinds; the UI falls back to a JSON dump for unknowns. */
  payload: Record<string, unknown>;
  created_at: string;
}

export interface SalesStrategyDocument {
  id: string;
  version: number;
  created_at: string;
  /** Markdown body the strategist wrote. The Strategy tab renders it
   * with a sanitiser; the Today tab pulls a TL;DR paragraph. */
  body_markdown: string;
  /** Pre-extracted TL;DR for the Today tab — the strategist outputs
   * a 1-paragraph summary alongside the full memo so the Today tab
   * doesn't have to do markdown surgery. */
  tldr: string;
}

export interface SalesStrategyHistoryPayload {
  versions: Array<Pick<SalesStrategyDocument, "id" | "version" | "created_at">>;
}

/* ── Strategy folds (2026-08-27) ──────────────────────────────────────
 * A steering note used to be saved and then the operator waited out a full
 * Sonnet call before the request answered — and a failed round trip reported a
 * hard failure over a note that had already landed. The regeneration is now a
 * job the agent runs behind the operator; this is what the dashboard sees of
 * it. */

export type SalesStrategyFoldState = "queued" | "running" | "done" | "failed";

export interface SalesStrategyFold {
  id: string;
  /** "steer" — a note triggered it; "refresh" — the operator pressed
   *  Strategy ▸ Refresh now. */
  reason: "steer" | "refresh";
  state: SalesStrategyFoldState;
  /** Burned at claim time, so a worker that died still counts. 3 = given up. */
  attempts: number;
  /** The agent's own words for the last failure. Shown verbatim — "gateway
   *  unavailable" must not be flattened into "failed". */
  error: string;
  /** The StrategyDocument version this fold wrote; null until it succeeds. */
  strategy_version: number | null;
}

/** Still something to wait for. Drives the 30 s → 5 s poll switch. */
export function isFoldInFlight(fold: SalesStrategyFold | null | undefined): boolean {
  return fold?.state === "queued" || fold?.state === "running";
}

/** The chip text, or null when there is no chip to render.
 *
 * A FAILURE returns null on purpose: it gets a full sentence and a Retry
 * button, not a three-word pill. A directive posted before folds were tracked
 * has no job at all and must render nothing rather than an invented state. */
export function foldChipLabel(
  fold: SalesStrategyFold | null | undefined,
): string | null {
  if (!fold) return null;
  if (isFoldInFlight(fold)) {
    // A *queued* job with >=1 attempt already failed and was requeued for
    // retry. A *running* job on attempt 1 is on its first try — attempts is
    // only incremented at claim time, so "running" alone never means retry.
    return fold.attempts > 1 || (fold.state === "queued" && fold.attempts > 0)
      ? "Folding into strategy… (retrying)"
      : "Folding into strategy…";
  }
  if (fold.state === "done") {
    return fold.strategy_version
      ? `Folded into strategy v${fold.strategy_version}`
      : "Folded into strategy";
  }
  return null;
}

/** A failed fold's `error` (or the regen job's, on the Strategy tab) is
 *  shown VERBATIM in the operator-facing sentence — "gateway unavailable"
 *  must not be flattened into "failed". But the sweeper is the only writer
 *  of a genuinely blank one (near-unreachable: every other failure path sets
 *  a real message), and an interpolated "" reads as "Couldn't fold this into
 *  the strategy — . The directive is…" — a stray dash with nothing after it.
 *  Fall back to a neutral clause instead of ever rendering that. */
export function foldFailureReason(error: string): string {
  return error.trim() || "the agent didn't say why";
}

export interface SalesSteeringAck {
  ok: true;
  message: string;
  id: string;
  target: SalesSteeringTarget;
  fold: SalesStrategyFold | null;
}

export interface SalesStrategyRefreshAck {
  ok: true;
  message: string;
  /** Null when the enqueue itself was swallowed (e.g. a failed write) rather
   * than failing the request outright — there is nothing already committed
   * to fail over, so the endpoint still answers 202 with no job to point at.
   * Never a fabricated id. */
  job: SalesStrategyFold | null;
}

export interface SalesSteeringNote {
  id: string;
  created_at: string;
  author_email: string;
  body: string;
  /** Which agent this steer aims at (2026-06-21). Optional for back-compat with
   * pre-target payloads; absent ⇒ a Lead-Generator steer. Coerce via
   * `steerTarget()`. */
  target?: SalesSteeringTarget;
  /** When the agent first ACKNOWLEDGED this directive (saw it + started
   * acting). v2: this no longer retires the note — an acknowledged
   * directive is still in force. null until acknowledged. */
  consumed_at: string | null;
  /** v2: when the directive was RETIRED (archived). null == still an
   * active standing directive the agent honours every cycle. The Today
   * payload may omit this field (it predates the directive model); the
   * dedicated `/v2/steering/active` endpoint always sets it. */
  archived_at?: string | null;
  /** HARVESTER notes only (2026-08-07): the campaign this note changed.
   * null/absent means it never reached one — either it predates the change
   * (it was read as a matrix constraint back then and is now inert history)
   * or it was posted through the raw API. The dialog offers "Apply to
   * campaign" on those rather than re-interpreting them silently. */
  applied_campaign?: string | null;
  applied_at?: string | null;
}

/** A steering DIRECTIVE as returned by `/v2/steering/active` (v2). A
 * directive is a STANDING order: it keeps shaping the agent's missions,
 * strategy, and lead-hunting every cycle until the operator archives it.
 * `consumed_at` only records acknowledgement — it does NOT retire the
 * directive. */
export interface SalesSteeringDirective {
  id: string;
  created_at: string;
  author_email: string;
  body: string;
  /** Which agent this directive steers (2026-06-21): "leads" | "harvester" |
   * "both". Optional for back-compat; absent ⇒ "leads". Coerce via
   * `steerTarget()`. */
  target?: SalesSteeringTarget;
  consumed_at: string | null;
  archived_at: string | null;
  /** archived_at == null. */
  is_active: boolean;
  /** consumed_at != null — the agent has seen + started acting on it. */
  acknowledged: boolean;
  /** Per-steer progress (v2 — counted from the [steer:id8] mission tag), so a
   * stuck directive is never invisible. `missions_active` = pending+running. */
  missions_total?: number;
  missions_active?: number;
  missions_done?: number;
  /** queued (not picked up) · pursuing (running missions now) · acting
   * (some ran, budget left) · exhausted (spent its mission budget without
   * producing — likely un-groundable; re-issue or archive). */
  status?: "queued" | "pursuing" | "acting" | "exhausted";
  /** The agent's EXHAUSTION VERDICT, written once it has tried everything for
   * the steer (spent its full dedicated mission budget): a short first-person
   * note on what it tried, why the public web could/couldn't confirm it, and
   * what to do next. Empty until exhausted. Surfaced verbatim in the directive
   * card so the operator never sees a silent drift. */
  verdict?: string;
  /** The agent's recommendation accompanying the verdict:
   * reframe (theme good, literal target un-groundable — try a findable angle) ·
   * archive (not XOP's market / nothing left) · keep (still promising). */
  verdict_action?: "reframe" | "archive" | "keep" | null;
  /** When the verdict was written (ISO). */
  verdict_at?: string | null;
  /** HARVESTER directives only (2026-08-07) — the campaign this note changed.
   * See `SalesSteeringNote.applied_campaign`. */
  applied_campaign?: string | null;
  applied_at?: string | null;
  /** The strategy regeneration this directive triggered (2026-08-27). Absent
   *  or null on every directive posted before the fold became a tracked job. */
  fold?: SalesStrategyFold | null;
}

export interface SalesSteeringActivePayload {
  as_of: string;
  /** Active standing directives, newest first. */
  active: SalesSteeringDirective[];
}

export interface SalesTodayPayload {
  as_of: string;
  /** "Agent is healthy" / "Agent is bootstrapping" / "Agent paused…" /
   * etc. — already formatted for direct display so the UI doesn't have
   * to switch on health codes a second time. When `is_paused`, this
   * reads "Agent paused…" (operator pause is the top-level truth). */
  status_line: string;
  /** Operator pause overrides every other state: a paused agent is not
   * "healthy / Live". The hero derives a warn tone + shows one paused
   * status from this rather than string-matching `status_line`. */
  is_paused?: boolean;
  paused_reason?: string;
  /** Free-form label the agent picked for its current mission, e.g.
   * "Trader-voice replacement opportunities in EMEA banking". null
   * when the agent is idle between missions. */
  current_mission_label: string | null;
  /** The agent's most recent reflection sentence — the "what I think"
   * line the operator skims when checking whether the agent is
   * actually thinking. null on cold start. */
  last_reflection: string | null;
  /** This-month TL;DR pulled from the latest StrategyDocument.tldr. */
  weekly_mission_paragraph: string | null;
  /** When that memo was written / stops being current (ISO). Optional: an
   * agent older than 2026-09-23 doesn't send them. The Status card dates the
   * memo from these so a stale one can't pass as "This month". */
  weekly_mission_written_at?: string | null;
  weekly_mission_expires_at?: string | null;
  /** Up to 10 most-recently-updated leads, sorted by last_signal_at desc.
   * NOTE: the Today tab no longer renders this — it shows "leads added
   * today" via a dedicated `/v2/leads?created_after=<local-midnight>`
   * query (this list is not date-scoped). Kept in the contract for
   * backward compatibility / other consumers. */
  fresh_leads: SalesOrgLeadCard[];
  /** Last 3 steering notes the operator dropped, regardless of
   * consumption — gives them a reference for what they've already
   * told the agent. */
  recent_steering_notes: SalesSteeringNote[];
  /** Daily-budget "sleep" state. When `budget_exhausted`, the agent has
   * spent its daily LLM budget and is idling until `budget_resets_at` — the
   * hero shows "Agent sleeping…" (already baked into `status_line`) rather
   * than a misleading "healthy / Live". */
  budget_exhausted?: boolean;
  budget_resets_at?: string | null;
  spend_today_usd?: number;
  daily_budget_usd?: number;
}

export interface SalesBrainFeedPayload {
  as_of: string;
  items: SalesAgentAction[];
}

export interface SalesBrainMissionsPayload {
  as_of: string;
  items: SalesMission[];
}

/** Generic ack envelope for v2 POST endpoints (feedback, steering,
 * strategy refresh, mission run-now). The agent surfaces a 1-line
 * confirmation the UI can flash as a toast. */
export interface SalesV2Ack {
  ok: true;
  message: string;
  /** When the action enqueues background work, the agent returns the
   * task id so the Brain tab can show it in the activity feed. */
  task_id?: string;
}

/* ── v0 — legacy procurement-notice scan ───────────────────────────────── */

export type SalesOpportunityStatus =
  | "new"
  | "reviewed"
  | "qualified"
  | "rejected"
  | "archived";

export type SalesOpportunityCategory =
  | "aviation_it"
  | "airport_networks"
  | "comms"
  | "border_control"
  | "passenger_systems"
  | "other"
  | "off_topic";

export interface SalesKpi {
  key: string;
  label: string;
  value: number | string;
  /** Optional period-over-period delta. */
  delta?: number;
  /** Optional unit hint (e.g. "USD", "%", or undefined for raw counts). */
  unit?: string;
}

export interface SalesOpportunityCard {
  id: string;
  title: string;
  /** ISO 3166-1 alpha-2 country code, or "" if upstream didn't tag one. */
  country: string;
  estimated_value: number | null;
  currency: string;
  closes_on: string | null;
  category: SalesOpportunityCategory;
  /** 0..1 confidence that this fits XOP. */
  confidence: number;
  source_slug: string;
  canonical_url: string;
  status: SalesOpportunityStatus;
  published_at: string | null;
}

export interface SalesSourceHealth {
  slug: string;
  name: string;
  region: string;
  cadence: "fast" | "default" | "slow" | string;
  enabled: boolean;
  last_success_at: string | null;
  last_scanned_at: string | null;
  consecutive_failures: number;
  last_error: string | null;
}

export interface SalesScanFeedItem {
  id: string;
  source_slug: string | null;
  cadence: string;
  status: "queued" | "running" | "succeeded" | "failed" | "partial" | string;
  started_at: string | null;
  finished_at: string | null;
  items_seen: number;
  items_new: number;
  items_classified: number;
}

export interface SalesConfidenceBucket {
  lower: number;
  upper: number;
  count: number;
}

export interface SalesDashboardPayload {
  /** Server timestamp at the moment the payload was assembled. */
  as_of: string;
  kpis: SalesKpi[];
  top_opportunities: SalesOpportunityCard[];
  /** Country code → opportunity count. Sorted high-to-low by the UI. */
  by_country: Record<string, number>;
  /** Category → opportunity count, both qualified and pending. */
  by_category: Record<string, number>;
  /** 10 buckets [0..0.1), [0.1..0.2), … [0.9..1.0]. */
  confidence_histogram: SalesConfidenceBucket[];
  sources: SalesSourceHealth[];
  recent_scans: SalesScanFeedItem[];
  /** Hard guardrail — Phase 6 forces this `false`. The UI should refuse to
   * render outbound controls unless this is `true`. */
  outbound_enabled: boolean;
}

export interface SalesActivityItem {
  id: string;
  action: string;
  summary: string;
  source_slug: string | null;
  opportunity_id: string | null;
  scan_run_id: string | null;
  payload: Record<string, unknown>;
  created_at: string;
}

export interface SalesActivityPayload {
  as_of: string;
  items: SalesActivityItem[];
}

export interface SalesBuyer {
  name: string;
  country: string;
  opportunity_count: number;
  avg_confidence: number;
}

export interface SalesBuyersPayload {
  as_of: string;
  items: SalesBuyer[];
}

// ── Capability map — the agent's self-expanding solution-fit catalog ──────
// The curated catalog (read-only) is what solution-led discovery already
// hunts; proposals are NEW capability→vertical fits the agent discovered from
// the KB for the operator to approve/reject in Sales ▸ Knowledge. Approved
// proposals become live discovery angles.

export interface SalesCapabilityVertical {
  vertical: string;
  use_case: string;
}

export interface SalesCapabilityCatalogEntry {
  product: string;
  capability: string;
  job: string;
  verticals: SalesCapabilityVertical[];
}

export interface SalesCapabilityCatalogPayload {
  entries: SalesCapabilityCatalogEntry[];
}

export interface SalesCapabilityProposal {
  id: string;
  /** The XOP capability/product this fit is built on. */
  capability: string;
  /** The non-obvious vertical/market proposed. */
  vertical: string;
  /** The specific operational workflow the capability serves there. */
  use_case: string;
  /** KB-grounded reasoning for why it fits. */
  rationale: string;
  /** The KB feature/doc the rationale leaned on (provenance). */
  kb_source: string;
  /** pending → awaiting review · approved → live angle · rejected → declined. */
  status: "pending" | "approved" | "rejected";
  /** ISO 8601 — when the agent proposed it. */
  created_at: string;
  /** ISO 8601 — when the operator decided (null while pending). */
  decided_at?: string | null;
  /** Operator who decided (email/sub), empty while pending. */
  decided_by?: string;
}

export interface SalesCapabilityProposalsPayload {
  as_of: string;
  proposals: SalesCapabilityProposal[];
}

/* ── Harvest campaigns (Sales ▸ Campaigns) ───────────────────────────
 *
 * Operator-defined, time-boxed target sets: "August is banking month".
 * Before these, the harvester's verticals were hardcoded in the agent's
 * Python, so a sector it didn't already know could not be targeted at all —
 * a steer naming one matched nothing, and harvest steering fails OPEN, so
 * "hunt ONLY banking" was silently read as "no restriction". */

export type SalesCampaignStatus = "draft" | "active" | "archived";

export interface SalesCampaignVertical {
  vertical: string;
  label: string;
  /** What the enumerator searches for. `{region}` is substituted per cell.
   *  A vertical with none of these is rejected by the agent: it would sweep
   *  nothing and still mark its cells covered. */
  enumeration_queries: string[];
  /** Authoritative directory pages fetched alongside the search results. */
  registry_urls: string[];
}

export interface SalesCampaign {
  id: string;
  /** Join key onto every harvested org/contact/mission. NOT renameable. */
  slug: string;
  label: string;
  goal: string;
  status: SalesCampaignStatus;
  starts_on: string | null;
  ends_on: string | null;
  verticals: SalesCampaignVertical[];
  /** Empty = the whole world region list. */
  regions: string[];
  priority: number;
  created_by: string;
  created_at: string | null;
  /** Derived server-side so the UI never re-implements the window rule and
   *  disagrees with the sweep about what is running. */
  in_window: boolean;
  is_live: boolean;
}

export interface SalesCampaignsPayload {
  campaigns: SalesCampaign[];
  /** What the harvester is ACTUALLY sweeping right now — live campaigns if
   *  any, else the code catalog. Without this the UI could show a draft and
   *  imply it is running. */
  effective_campaigns: string[];
  using_default_catalog: boolean;
  available_regions: string[];
}

/**
 * `POST /api/sales/v2/harvest/campaigns/:slug/contact-group` — put ONE
 * campaign's contacts into ONE CRM contact group ("Campaign: <label>") that an
 * email campaign can target. The group holds exactly that campaign's sendable
 * contacts and nothing else: pressing the button again REBUILDS it.
 */
export interface SalesCampaignContactGroupResult {
  campaign: string;
  label: string;
  group: { id: number; name: string; members_count: number };
  /** Campaign contacts that have an email at all. */
  with_email: number;
  /** Put in the group — the sendable subset of `with_email`. */
  added: number;
  /** Left out: the harvester found no mail server on the domain, or the
   *  verifier rejected the address. Mailing them only bounces. */
  skipped_unsendable: number;
  /** New directory rows vs. addresses the CRM already knew. */
  created: number;
  matched: number;
  /** The campaign had more than the 10,000-contact ceiling. */
  capped: boolean;
}

export interface SalesCampaignReport {
  campaign: string;
  label: string;
  contacts: {
    total: number;
    with_email: number;
    /** SMTP-accepted. The number a webinar invite list can actually use. */
    verified_email: number;
    /** Pattern guesses — counted apart so the headline isn't flattered. */
    guessed_email: number;
    with_linkedin: number;
  };
  contacts_by_seniority: Record<string, number>;
  orgs: { total: number; with_domain: number; with_contacts: number };
  coverage: {
    cells_total: number;
    cells_done: number;
    cells_pending: number;
    cells_running: number;
    cells_failed: number;
    percent_complete: number;
  };
  spend: {
    cost_usd: number;
    calls: number;
    input_tokens: number;
    output_tokens: number;
    by_caller: Record<string, number>;
    /** Per-bot rollup of `by_caller` — keys `harvester` / `lead_generator` /
     *  `other`. Computed server-side (xop-agents `llm_bots.py`); never derive
     *  it in the SPA from caller tags. Absent on a pre-2026-09-05 agent. */
    by_bot?: Record<string, number>;
    /** Harvester spend carrying NO campaign tag at all, campaign-wide. Not
     *  attributable to this or any campaign — reported so a small `cost_usd`
     *  reads as "this is all we can see" instead of as the bill. Until
     *  2026-09-05 the dig pass (the expensive half) never tagged itself, so
     *  historical rows are all in here and can never be split out. */
    unattributed_usd?: number;
  };
  leads: {
    promoted: number;
    rejected: number;
    exists_in_crm: number;
    by_promotion_state: Record<string, number>;
    lead_status: Record<string, number>;
  };
  per_vertical: Array<{
    vertical: string;
    orgs: number;
    contacts: number;
    emails: number;
  }>;
  /** null (not 0) when nothing was found — "$0.00 per contact" reads as free
   *  when the truth is "spent money, produced nothing to divide by". */
  cost_per_contact_usd: number | null;
  cost_per_verified_email_usd: number | null;
}

export interface SalesCampaignInput {
  label: string;
  goal?: string;
  status?: SalesCampaignStatus;
  starts_on?: string | null;
  ends_on?: string | null;
  verticals: SalesCampaignVertical[];
  regions?: string[];
  priority?: number;
}

// ── Steering the harvester = editing the live campaign (2026-08-07) ─────
//
// One text box changes what the harvester hunts. The operator types a note in
// plain words and the agent resolves it into a PATCH on the live campaign,
// which the dialog renders as a diff ABOVE the Send button — one field, one
// button, no second screen, and no surprise about what a sentence did to a
// live sweep.
//
// This replaced a model where a steer was re-read on every beat as a
// CONSTRAINT over the target matrix, from a closed keyword vocabulary. That is
// how the 2026-08-06 note ("investments banks, private wire networks, trader
// voice networks in USA and Europe") landed its two place words and silently
// discarded all three sectors: extraction only began at a trigger word.

/** One sector the harvester should hunt. `vertical` is the canonical slug the
 *  target matrix joins on; `label` is what the operator reads. */
export interface SalesSteerSector {
  vertical: string;
  label: string;
}

/** How a note's sector list relates to the campaign's current one.
 *  `replace` is the default — "here's what to hunt" reads as the whole
 *  instruction, not an amendment — and the operator can flip it in the
 *  preview, which is the one call a machine genuinely can't make for them. */
export type SalesSteerMode = "replace" | "add";

/** What the agent read out of the note. An EMPTY axis means "leave that axis
 *  alone", never "clear it" — so a note with no sectors is a legitimate
 *  strategy change (it rewrites the goal the query planner reads). */
export interface SalesSteerPatch {
  sectors: SalesSteerSector[];
  regions: string[];
  goal: string;
  mode: SalesSteerMode;
  /** "model" when the gateway resolved it, "fallback" when the deterministic
   *  parser did. Surfaced so a blunt read is a visible state, not a silent
   *  one — the operator is confirming it, after all. */
  source: "model" | "fallback";
  note: string;
}

/** The change a patch would make to the live campaign. Rendered as the diff
 *  above the Send button. */
export interface SalesSteerPreview {
  resolved: SalesSteerPatch;
  campaign: {
    /** null when nothing is live — sending would CREATE the campaign. */
    slug: string | null;
    label: string;
    is_new: boolean;
  };
  sectors: {
    added: SalesSteerSector[];
    removed: SalesSteerSector[];
    kept: SalesSteerSector[];
    /** The campaign's full sector list afterwards. */
    result: SalesSteerSector[];
    mode: SalesSteerMode;
  };
  /** An empty region list means WORLDWIDE (render it that way, don't show ""). */
  regions: { before: string[]; after: string[]; changed: boolean };
  goal: { before: string; after: string; changed: boolean };
  changed: boolean;
  /** False ⇒ the patch would leave the campaign with no sectors and the agent
   *  will refuse it. Disable Send. */
  applicable: boolean;
  warnings: string[];
}

export interface SalesSteerApplyResult {
  campaign: SalesCampaign;
  diff: Omit<SalesSteerPreview, "resolved">;
  note_id: string;
  created: boolean;
  /** Queued cells re-pointed at the campaign's current search queries. */
  missions_repointed: number;
  /** "both" notes only (2026-08-27): the id of the strategy-regen job the
   *  agent enqueued to fold in the directive's lead half — replaces the old
   *  synchronous `strategy_regenerated: boolean` now that the regen runs as
   *  a background job instead of blocking this request. Null when the
   *  target was harvester-only (nothing to fold), or when the enqueue
   *  itself failed and was swallowed (the strategist folds it in on its
   *  next pass either way). */
  fold_id: string | null;
}
